import { StudyDemoTutorReplay } from '@/pages/try/studyExperience/StudyDemoTutorReplay';
import { demoTutorForContext } from '@/pages/try/studyExperience/demoTutorExamples';
import { isLocalStudyExperience } from '@/pages/try/studyExperience/mode';
import { useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ImagePlus, X } from 'lucide-react';
import { DEFAULT_TUTOR_NAME, TutorAvatar } from '@/components/tutor/TutorAvatar';
import { ToolContextPreview } from './ToolContextPreview';
import { WorkingSheet } from './WorkingSheet';
import { useWorkingOverlay, WORKING_PANEL } from './workingOverlay';
import { ApiError } from '@/lib/api';
import { OutOfStarsSheet } from '@/components/OutOfStarsSheet';
import { recordHostedInteraction } from '@/lib/studyToolTelemetry';
import { STUDY_TOOL_CACHE_PREFIX } from '@/auth/studyToolCache';
import { getKidSub } from '@/auth/authStore';
import { AcademyRichText } from '../academy/AcademyRichText';
import { getSavedWorking } from './workingCanvasApi';
import { getMathTutorPrice, getToolTutorPrice } from './studyApi';
import { renderWorkingImage, workingCheckKey } from './workingTutorApi';
import { sendTutorMessage, sendMathToolMessage } from './tutorChat';
import { useWorkingTutorConversation } from './workingTutorConversation';
import { workingTutorActorGeneration } from './workingTutorReturnCache';
import { MAX_TUTOR_IMAGES, prepareTutorImage, tutorImageUrl } from './tutorImages';
import { TutorMessage } from './TutorMessage';
import { workingContentSchema, type WorkingContent } from './workingCanvas';

// PostgreSQL jsonb and schema-parsed replies can order object keys differently.
// Keep drawing/array order significant while comparing the same content shape.
function workingFingerprint(content: WorkingContent): string {
  return JSON.stringify(workingContentSchema.parse(content));
}
import { useCachedStudentManager } from '@/auth/familyManager';
export interface MathTutorContext {
  subject_key?: 'mathematics' | 'chemistry' | 'physics';
  tool_id: string;
  name: string;
  year: number;
  snapshot: string;
  session_id: string;
  version: number;
}
interface Props {
  tool?: MathTutorContext;
  conversation?: ReturnType<typeof useWorkingTutorConversation>;
  content: WorkingContent;
  disabled: boolean;
  assessment: boolean;
  flush: () => Promise<string | null>;
}
/** Backend code when the family wallet cannot pay the Tutor fee (no Stars are taken). */
const WALLET_INSUFFICIENT = 'WALLET_INSUFFICIENT';
const TUTOR_OUT_OF_STARS_BODY =
  'The Tutor needs Stars to answer, and your family’s Star tank is empty. No Stars were taken, and your message and working are kept.';
const TUTOR_OUT_OF_STARS_ERROR =
  'Out of Stars. Ask a parent to top up in the Family Portal, then prepare this message again.';
const TUTOR_OUT_OF_STARS_STUDENT_ERROR =
  'Out of Stars. Top up your Stars in My account, then prepare this message again.';
const terminalCodes = new Set([
  WALLET_INSUFFICIENT,
  'WORKING_TUTOR_INPUT_INVALID',
  'MODERATION_REJECTED',
  'MODERATION_UNAVAILABLE',
  'LLM_UPSTREAM_FAILED',
  'LLM_OUTPUT_INVALID',
  'LLM_OUTPUT_REJECTED',
  'WORKING_TUTOR_REPLY_INVALID',
  'AI_OPERATION_RECEIPT_FAILED',
  'AI_OPERATION_FAILED',
  'AI_OPERATION_EXPIRED',
  'AI_OPERATION_CONTEXT_CHANGED',
  'AI_PRICE_CHANGED',
  'WORKING_CANVAS_VERSION_CHANGED',
  'WORKING_TUTOR_HISTORY_UNAVAILABLE',
]);
export function WorkingTutorPanel({
  content,
  disabled,
  assessment,
  flush,
  conversation,
  tool,
}: Props) {
  // An independent student tops up their own Stars (D-SSU-23).
  const studentManager = useCachedStudentManager();
  const priceDescriptionId = useId();
  const overlay = useWorkingOverlay();
  const open = overlay.panel === WORKING_PANEL.TUTOR;
  const localConversation = useWorkingTutorConversation(
    tool ? 'Help me understand this tool.' : undefined,
    tool ? `tool:${tool.subject_key ?? 'mathematics'}:${tool.tool_id}:${tool.year}` : '',
  );
  const currentConversation = conversation ?? localConversation;
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const requestedTurn = params.get('workingTutorTurn');
  const consumedReturn = useRef('');
  const [returnIssue, setReturnIssue] = useState<string | null>(null);
  const {
    uploads,
    setUploads,
    message,
    setMessage,
    turns,
    setTurns,
    pending,
    setPending,
    busy,
    setBusy,
    error,
    setError,
    terminal,
    setTerminal,
    active,
  } = conversation ?? localConversation;
  const { setPanel } = overlay;
  const workingVersion = params.get('workingVersion');
  useEffect(() => {
    const initial = tool ? 'Help me understand this tool.' : 'Help me check my working.';
    if (
      !requestedTurn ||
      busy ||
      pending ||
      (message.trim() && message !== initial) ||
      consumedReturn.current === `${location.key}:${requestedTurn}`
    )
      return;
    consumedReturn.current = `${location.key}:${requestedTurn}`;
    const saved = turns.find(
      (turn) => turn.id === requestedTurn && String(turn.version) === workingVersion,
    );
    setReturnIssue(
      !saved
        ? 'This temporary explanation is no longer here. Your saved working is separate; no new AI started.'
        : saved.sourceFingerprint &&
            saved.sourceFingerprint !== (tool ? tool.snapshot : workingFingerprint(content))
          ? `This explanation belongs to saved working version ${saved.version}. The current drawing or tool settings changed.`
          : null,
    );
    setPanel(WORKING_PANEL.TUTOR);
    const frame = requestAnimationFrame(() =>
      document
        .getElementById(`working-tutor-turn-${requestedTurn}`)
        ?.scrollIntoView?.({ block: 'nearest' }),
    );
    return () => cancelAnimationFrame(frame);
  }, [
    requestedTurn,
    turns,
    busy,
    pending,
    message,
    location.key,
    content,
    tool,
    workingVersion,
    setPanel,
  ]);
  // The Out-of-Stars sheet is panel UI only; the message stays in the conversation.
  const [outOfStars, setOutOfStars] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const top = useRef<HTMLDivElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  const isTool = !!tool;
  useEffect(() => {
    if (!open) return;
    if (isTool) {
      const body = bottom.current?.closest<HTMLElement>('.working-sheet-body');
      if (body) body.scrollTop = turns.length || pending || error ? body.scrollHeight : 0;
      return;
    }
    if (turns.length || pending || error) bottom.current?.scrollIntoView?.({ block: 'end' });
    else top.current?.scrollIntoView?.({ block: 'start' });
  }, [open, turns.length, pending, error, busy, isTool, overlay.portalTarget]);
  useEffect(() => {
    const body =
      isTool && open ? bottom.current?.closest<HTMLElement>('.working-sheet-body') : null;
    if (!body || typeof ResizeObserver === 'undefined') return;
    let atEnd = body.scrollHeight - body.clientHeight - body.scrollTop < 32;
    let height = body.clientHeight;
    const track = () => {
      // A resize can emit scroll before ResizeObserver; preserve the previous reading position.
      if (body.clientHeight === height)
        atEnd = body.scrollHeight - body.clientHeight - body.scrollTop < 32;
    };
    const observer = new ResizeObserver(() => {
      if (body.clientHeight !== height && atEnd) body.scrollTop = body.scrollHeight;
      height = body.clientHeight;
    });
    observer.observe(body);
    body.addEventListener('scroll', track);
    return () => {
      observer.disconnect();
      body.removeEventListener('scroll', track);
    };
  }, [isTool, open, overlay.portalTarget]);
  const fingerprint = tool ? tool.snapshot : workingFingerprint(content);
  const contextLabel = tool ? 'tool context' : 'working';
  const stale = pending !== null && pending.fingerprint !== fingerprint;
  const blocked = disabled || assessment;
  const tutorExpression = error
    ? 'reassuring'
    : busy
      ? 'thinking'
      : pending || message.trim()
        ? 'listening'
        : turns.length
          ? 'explaining'
          : 'friendly';
  async function prepare() {
    const preparedMessage =
      message.trim() || (uploads.length ? 'Help me understand the attached image.' : '');
    if (active.current || blocked || !preparedMessage || (error && pending && !terminal)) return;
    active.current = true;
    setBusy(true);
    setError('');
    setOutOfStars(false);
    try {
      if (tool && (tool.snapshot.length > 12000 || tool.version > 1000000))
        throw new Error('This tool context is too large. Use a smaller example.');
      const id = tool ? tool.session_id : await flush();
      if (!id) throw new Error('Save your working before sending to Tutor.');
      const [page, price] = await Promise.all([
        tool
          ? Promise.resolve({ version: tool.version, content: { ...content, text: tool.snapshot } })
          : getSavedWorking(id),
        tool?.subject_key && tool.subject_key !== 'mathematics'
          ? getToolTutorPrice(tool.subject_key)
          : getMathTutorPrice(),
      ]);
      if (!Number.isInteger(price.stars) || price.stars < 1)
        throw new Error('Tutor pricing is unavailable. Try later.');
      const image = tool ? '' : renderWorkingImage(page.content);
      const previous = turns.slice(-8).map((t) => t.id);
      const payload = JSON.stringify({
        image,
        ...(tool
          ? {
              ...(tool.subject_key !== 'mathematics' ? { subject: tool.subject_key } : {}),
              tool: tool.tool_id,
              year: tool.year,
              snapshot: tool.snapshot,
            }
          : {}),
        message: preparedMessage,
        previous,
        attachments: uploads.map(({ mime_type, image_base64 }) => ({ mime_type, image_base64 })),
      });
      const base = await workingCheckKey(id, page.version, payload);
      const cacheKey = `${STUDY_TOOL_CACHE_PREFIX}working-chat-attempt:${getKidSub() ?? 'unknown'}:${base}`;
      let attempt = Number(localStorage.getItem(cacheKey) ?? '0');
      if (!Number.isSafeInteger(attempt) || attempt < 0)
        throw new Error('Tutor retry identity is unavailable. Reopen your working.');
      if (terminal) {
        attempt += 1;
        localStorage.setItem(cacheKey, String(attempt));
      }
      const key = attempt ? await workingCheckKey(id, page.version, payload, attempt) : base;
      setPending({
        id,
        uploads: [...uploads],
        fingerprint: tool ? tool.snapshot : workingFingerprint(page.content),
        text: page.content.text,
        request: {
          message: preparedMessage,
          previous_turns: previous,
          attachments: uploads.map(({ mime_type, image_base64 }) => ({ mime_type, image_base64 })),
          ...(tool
            ? {
                tool_context: {
                  subject_key: tool.subject_key,
                  tool_id: tool.tool_id,
                  year: tool.year,
                  snapshot: tool.snapshot,
                },
              }
            : {}),
          expected_version: page.version,
          expected_stars: price.stars,
          image_base64: image,
          idempotency_key: key,
        },
      });
      setTerminal(false);
    } catch (reason) {
      setError(
        reason instanceof ApiError && reason.code === 'WORKING_CANVAS_RETENTION_REQUIRED'
          ? 'Cloud saving is not enabled yet. Your draft stays on this device. Tutor can send it after saving is enabled.'
          : reason instanceof Error
            ? reason.message
            : 'Your message could not be prepared.',
      );
    } finally {
      active.current = false;
      setBusy(false);
    }
  }
  async function send() {
    if (!pending || active.current || blocked || (stale && !error) || terminal) return;
    recordHostedInteraction('study-tutor', 'tutor_submitted');
    active.current = true;
    setBusy(true);
    setError('');
    const actor = getKidSub();
    const actorGeneration = workingTutorActorGeneration();
    const generation = currentConversation.generation.current;
    const stillCurrent = () =>
      actor === getKidSub() &&
      workingTutorActorGeneration() === actorGeneration &&
      currentConversation.generation.current === generation;
    try {
      const result = await (tool ? sendMathToolMessage : sendTutorMessage)(
        pending.id,
        pending.request,
      );
      if (!stillCurrent()) return;
      setTurns((old) => [
        ...old,
        {
          id: result.turn_id,
          uploads: pending.uploads,
          message: pending.request.message,
          version: result.version,
          reply: result.reply,
          stars: result.stars_charged,
          sourceFingerprint: pending.fingerprint,
        },
      ]);
      setPending(null);
      setMessage('');
    } catch (reason) {
      if (!stillCurrent()) return;
      const outOfStars = reason instanceof ApiError && reason.code === WALLET_INSUFFICIENT;
      setOutOfStars(outOfStars);
      setTerminal(reason instanceof ApiError && terminalCodes.has(reason.code));
      setError(
        outOfStars
          ? studentManager
            ? TUTOR_OUT_OF_STARS_STUDENT_ERROR
            : TUTOR_OUT_OF_STARS_ERROR
          : reason instanceof Error
            ? reason.message
            : 'The result is unknown. Retry this same message.',
      );
    } finally {
      if (stillCurrent()) {
        active.current = false;
        setBusy(false);
      }
    }
  }
  async function attach(file: File | undefined) {
    if (!file || active.current || blocked || pending || uploads.length >= MAX_TUTOR_IMAGES) return;
    active.current = true;
    setBusy(true);
    setError('');
    try {
      const upload = await prepareTutorImage(file);
      setUploads((old) => [...old, upload]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'This image could not be prepared.');
    } finally {
      active.current = false;
      setBusy(false);
    }
  }
  function startNew() {
    if (busy || (pending && error && !terminal)) return;
    setUploads([]);
    setTurns([]);
    setPending(null);
    setMessage('');
    setError('');
    setTerminal(false);
  }
  if (isLocalStudyExperience()) {
    const example = demoTutorForContext(content.question, tool?.tool_id);
    return (
      <>
        <button
          type="button"
          aria-label="View prepared Tutor demo"
          aria-expanded={open}
          className="flex min-h-11 shrink-0 items-center gap-2 rounded-xl bg-wash-sky px-3 text-xs font-bold text-ink"
          onClick={() => {
            if (!overlay.fullScreen) {
              overlay.openFullScreen?.();
              overlay.setPanel(WORKING_PANEL.TUTOR);
            } else overlay.setPanel(open ? null : WORKING_PANEL.TUTOR);
          }}
        >
          <TutorAvatar animated={false} />
          <span>Demo Tutor</span>
        </button>
        {open && (
          <WorkingSheet
            title="Tuan Tuan · Tutor demo"
            label="Prepared Tutor demo"
            avatar={<TutorAvatar expression="explaining" animated={false} />}
            onClose={() => overlay.setPanel(null)}
            chat
          >
            <StudyDemoTutorReplay key={example.item.key} initialKey={example.item.key} />
          </WorkingSheet>
        )}
      </>
    );
  }
  return (
    <>
      <button
        type="button"
        data-usage-ignore
        aria-label={tool ? `Ask Tuan Tuan about ${tool.name}` : 'Ask Tutor to check my working'}
        aria-expanded={open}
        className="flex min-h-11 min-w-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-wash-sky px-2 text-xs font-extrabold text-ink min-[375px]:px-3"
        onClick={() => {
          if (!overlay.fullScreen) overlay.openFullScreen?.();
          overlay.setPanel(open ? null : WORKING_PANEL.TUTOR);
        }}
      >
        <TutorAvatar animated={false} />
        <span className={tool ? 'inline' : 'hidden min-[375px]:inline'}>Tutor</span>
      </button>
      {open && (
        <WorkingSheet
          chat
          usageTool={blocked ? undefined : 'study-tutor'}
          title={
            tool?.subject_key === 'chemistry'
              ? 'Chemistry Tutor'
              : tool?.subject_key === 'physics'
                ? 'Physics Tutor'
                : 'Math Tutor'
          }
          avatar={<TutorAvatar size="medium" expression={tutorExpression} />}
          subtitle={`${DEFAULT_TUTOR_NAME} · AI Tutor${tool ? ` · Year ${tool.year}` : ''}`}
          label={
            tool
              ? `${tool.subject_key === 'chemistry' ? 'Chemistry' : tool.subject_key === 'physics' ? 'Physics' : 'Math'} tool Tutor`
              : 'Tutor working check'
          }
          onClose={() => overlay.setPanel(null)}
          footer={
            <>
              {blocked ? (
                <p className="pb-3 text-sm font-bold">
                  {assessment
                    ? 'Tutor is off during assessments. Submit your exam before review.'
                    : 'Wait for your working to load or resolve its saving problem.'}
                </p>
              ) : (
                <>
                  {!pending && (
                    <div className={tool ? 'tutor-composer' : undefined}>
                      <input
                        ref={fileInput}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        aria-label="Choose Tutor image"
                        className="hidden"
                        disabled={busy}
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          event.target.value = '';
                          void attach(file);
                        }}
                      />
                      <div className="tutor-upload-controls flex items-center justify-between gap-2">
                        <button
                          type="button"
                          className="flex min-h-11 items-center gap-2 text-xs font-bold text-brand-sky disabled:opacity-40"
                          disabled={busy || uploads.length >= MAX_TUTOR_IMAGES}
                          onClick={() => fileInput.current?.click()}
                        >
                          <ImagePlus size={18} /> Add image
                        </button>
                        <span className="text-xs text-slate2">
                          {uploads.length}/{MAX_TUTOR_IMAGES}
                          {!tool && ' · JPG / PNG / WebP'}
                        </span>
                      </div>
                      {uploads.length > 0 && (
                        <>
                          <div
                            className="flex gap-2 overflow-x-auto pb-2"
                            aria-label="Images for next message"
                          >
                            {uploads.map((image, index) => (
                              <div key={index} className="relative shrink-0">
                                <img
                                  src={tutorImageUrl(image)}
                                  alt={`Attached photo ${index + 1}: ${image.name}`}
                                  className="h-16 w-20 rounded-xl border border-hairline object-contain bg-canvas-pure"
                                />
                                <button
                                  type="button"
                                  aria-label={`Remove image ${index + 1}`}
                                  disabled={busy}
                                  className="absolute -right-1 -top-1 flex h-11 w-11 items-center justify-center rounded-full bg-canvas-pure shadow-sm"
                                  onClick={() =>
                                    setUploads((old) => old.filter((_, i) => i !== index))
                                  }
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ))}
                          </div>
                          <p className="mb-2 text-xs text-slate2">
                            Included in your next messages until removed.
                          </p>
                        </>
                      )}
                      <label className="block text-xs font-bold">
                        Message Tutor
                        <textarea
                          aria-label="Message Tutor"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          maxLength={2000}
                          disabled={busy}
                          placeholder="Ask about your next step…"
                          className="input-k12 mt-2 min-h-20 w-full resize-none text-base"
                        />
                      </label>
                    </div>
                  )}
                  {pending && !error && (
                    <p className="mb-2 text-xs">
                      Ready to send · {contextLabel} version {pending.request.expected_version}
                    </p>
                  )}
                  {pending && stale && (
                    <p role="status" className="mb-2 text-xs font-bold">
                      {tool
                        ? 'Tool parameters changed. Preview the latest context before sending.'
                        : 'Your working changed. Attach the latest saved working before sending.'}
                    </p>
                  )}
                  <button
                    type="button"
                    className="btn-pill-primary my-2 w-full disabled:opacity-40"
                    aria-describedby={pending && !terminal ? priceDescriptionId : undefined}
                    disabled={
                      busy ||
                      blocked ||
                      (pending && !terminal ? stale && !error : !message.trim() && !uploads.length)
                    }
                    onClick={() => void (pending && !terminal ? send() : prepare())}
                  >
                    {busy
                      ? 'Tutor is working…'
                      : pending && !terminal
                        ? error
                          ? 'Retry same message'
                          : 'Send to Tutor'
                        : terminal
                          ? 'Prepare again'
                          : tool
                            ? 'Preview message & tool'
                            : 'Preview message & working'}
                  </button>
                  {pending && !terminal && (
                    <p id={priceDescriptionId} className="mb-2 text-center text-xs text-slate2">
                      {pending.request.expected_stars} Stars per message
                    </p>
                  )}
                  {pending && !busy && (!error || terminal) && (
                    <button
                      type="button"
                      className="mb-2 min-h-11 w-full text-xs font-bold"
                      onClick={() => {
                        setPending(null);
                        setError('');
                      }}
                    >
                      {tool
                        ? 'Edit message / refresh tool context'
                        : 'Edit message / attach latest working'}
                    </button>
                  )}
                </>
              )}
            </>
          }
        >
          <div ref={top} />
          {returnIssue && (
            <p role="status" className="rounded-xl bg-wash-sunshine p-3 text-sm">
              {returnIssue}
            </p>
          )}
          {!turns.length && !pending && !busy && !blocked && (
            <div
              className={
                tool
                  ? 'mb-4 flex items-center gap-3 rounded-2xl bg-wash-sky p-3'
                  : 'mb-5 flex flex-col items-center rounded-2xl bg-wash-sky px-4 py-5 text-center'
              }
            >
              <TutorAvatar size={tool ? 'medium' : 'large'} expression="greeting" />
              <div>
                <p className="mt-3 text-base font-extrabold text-ink">
                  Hi, I'm {DEFAULT_TUTOR_NAME}.
                </p>
                <p className="mt-1 text-sm text-slate2">
                  {tool
                    ? `Ask about ${tool.name}, its parameters or a photo. Let's take it one step at a time.`
                    : `Send your question, working or a photo. Let's take it one step at a time.`}
                </p>
              </div>
            </div>
          )}
          {tool ? (
            <details className="text-xs text-slate2">
              <summary className="min-h-11 cursor-pointer py-3 font-bold">
                Photos, Stars & chat history
              </summary>
              <p>
                Add up to 3 JPG, PNG or WebP photos (8 MB each). Review the Star price before
                sending. Recent delivered replies can return within this browser for up to one hour.
                Reloading ends that temporary history. Photos are not kept for navigation returns.
                Tutor does not award marks.
              </p>
            </details>
          ) : (
            <p className="text-xs text-slate2">
              Ask a question, add up to 3 photos (8 MB each), or send your latest {contextLabel}.
              Each message shows its Star price before sending. Chats here last for this open{' '}
              {tool ? 'tool' : 'working'} session; earlier results are available to the server for
              24 hours. Tutor does not award marks.
            </p>
          )}
          <button
            type="button"
            className="my-2 min-h-11 text-xs font-bold text-brand-sky disabled:opacity-40"
            disabled={busy || !!(pending && error && !terminal)}
            onClick={startNew}
          >
            New conversation
          </button>
          <div role="log" aria-label="Tutor conversation" aria-live="polite" className="space-y-4">
            {turns.map((turn) => (
              <div key={turn.id} className="space-y-3">
                <article
                  aria-label="Student message"
                  className="ml-8 whitespace-pre-wrap rounded-2xl bg-wash-sky p-3"
                >
                  <p className="mb-2 text-xs font-bold text-slate2">
                    You · {contextLabel} v{turn.version}
                  </p>
                  <AcademyRichText text={turn.message} />
                  {turn.uploads.map((image, index) => (
                    <img
                      key={index}
                      src={tutorImageUrl(image)}
                      alt={`Sent photo ${index + 1}: ${image.name}`}
                      className="mt-2 max-h-40 w-full rounded-xl object-contain bg-canvas-pure"
                    />
                  ))}
                </article>
                <article
                  id={`working-tutor-turn-${turn.id}`}
                  aria-label="Tutor reply"
                  className="mr-3 rounded-2xl border border-hairline bg-canvas-pure p-4"
                >
                  <div className="mb-3 flex items-center gap-2">
                    <TutorAvatar expression="explaining" animated={false} />
                    <p className="text-xs font-bold text-slate2">
                      {DEFAULT_TUTOR_NAME} · Tutor · {turn.stars} ★ · {contextLabel} v{turn.version}
                    </p>
                  </div>
                  <TutorMessage
                    reply={turn.reply}
                    contextDisabled={busy || Boolean(message.trim()) || uploads.length > 0}
                    sourceVersion={turn.version}
                    returnTo={`${location.pathname}?${new URLSearchParams({ ...Object.fromEntries(params), workingTutorTurn: turn.id, workingVersion: String(turn.version) })}`}
                    subjectKey={
                      !assessment && turn === turns.at(-1)
                        ? (tool?.subject_key ?? 'mathematics')
                        : undefined
                    }
                  />
                  {Boolean(turn.omittedPhotos) && (
                    <p className="mt-2 text-xs text-slate2">
                      Photos are not retained in this temporary chat return. Your saved working is
                      separate.
                    </p>
                  )}
                  <div className="mt-3 flex flex-col gap-2">
                    {turn.reply.suggested_questions.map((question) => (
                      <button
                        type="button"
                        key={question}
                        disabled={!!pending || busy}
                        onClick={() => setMessage(question)}
                        className="min-h-11 rounded-xl bg-wash-sky p-2 text-left text-xs font-bold disabled:opacity-40"
                      >
                        {question}
                      </button>
                    ))}
                  </div>
                </article>
              </div>
            ))}
            {pending && (
              <article
                aria-label="Prepared student message"
                className="ml-6 whitespace-pre-wrap rounded-2xl bg-wash-sky p-3"
              >
                <p className="mb-2 text-xs font-bold">
                  You · {busy ? 'sending' : 'not sent'} · {contextLabel} v
                  {pending.request.expected_version}
                </p>
                <AcademyRichText text={pending.request.message} />
                {pending.uploads.map((image, index) => (
                  <img
                    key={index}
                    src={tutorImageUrl(image)}
                    alt={`Photo ${index + 1} to send: ${image.name}`}
                    className="mt-2 max-h-40 w-full rounded-xl object-contain bg-canvas-pure"
                  />
                ))}
                <details open={!turns.length} className="mt-3">
                  <summary className="min-h-11 cursor-pointer text-xs font-bold">
                    {tool ? `Attached tool context · ${tool.name}` : 'Attached saved working'}
                  </summary>
                  <div className="max-h-44 overflow-auto rounded-xl bg-canvas-pure p-2">
                    {tool ? (
                      <ToolContextPreview snapshot={pending.text} />
                    ) : (
                      <AcademyRichText text={pending.text} />
                    )}
                    {!tool && (
                      <img
                        alt="Saved handwriting to send to Tutor"
                        className="mt-2 w-full"
                        src={`data:image/png;base64,${pending.request.image_base64}`}
                      />
                    )}
                  </div>
                </details>
              </article>
            )}
          </div>
          {busy && (
            <div
              role="status"
              aria-label="Tutor status"
              className="mt-4 flex items-center gap-3 text-sm text-slate2"
            >
              <TutorAvatar expression="thinking" />
              <p>{pending ? `${DEFAULT_TUTOR_NAME} is thinking…` : 'Preparing your message…'}</p>
            </div>
          )}
          {error && (
            <p role="alert" className="mt-4 rounded-xl bg-wash-coral p-3 text-sm font-bold">
              {error}
            </p>
          )}
          {outOfStars && (
            <OutOfStarsSheet
              body={TUTOR_OUT_OF_STARS_BODY}
              dismissLabel="Keep working without the Tutor"
              onDismiss={() => setOutOfStars(false)}
            />
          )}
          {terminal && (
            <p className="mt-2 text-xs">
              This request ended. Prepare again and review its price. Your earlier messages are
              kept.
            </p>
          )}
          <div ref={bottom} />
        </WorkingSheet>
      )}
    </>
  );
}
